import { useEffect, useCallback } from 'react';
import { type Subscription } from 'rxjs';
import { TranscriptionCapability } from '@doxyme/extensions-bridge';
import { useAddCapability } from '@doxyme/extensions-bridge-react';
import { _getLogger } from '@doxyme/analytics';
import { bridge } from '../../../features/extensions/instances';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { useService } from '../../useService';
import { useAppSelector } from '../../useReduxToolkit';

const logger = _getLogger('useExtensionsAddTranscription', {
  component: 'extensions',
});

const fetchBlob = async (url: string): Promise<Blob | undefined> => {
  try {
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(
        `Audio fetch failed: ${response.status} ${response.statusText}`,
      );
    return response.blob();
  } catch (err) {
    logger.error('Failed to fetch audio blob', err);
    return undefined;
  }
};

/**
 * Hook responsible for providing callbacks required for transcription to the Extensions SDK
 */
export function useExtensionsAddTranscription(): void {
  const transcription = useAddCapability(
    bridge,
    () => new TranscriptionCapability(),
  );

  const { sessionId } = useAppSelector(getApolloSessionDetails);

  const service = useService('transcription');

  const subscribeToTranscriptionStatus = useCallback((): Subscription => {
    return service.transcriptionStatus.subscribe(
      async ({ isSuccess, transcriptionId, errorMessage, data }) => {
        if (!isSuccess) {
          transcription.completed({
            isSuccess,
            transcriptionId,
            errorMessage: errorMessage as string,
          });
          void service.delete(transcriptionId);
          return;
        }

        let isComplete = false;

        if (data?.url !== undefined) {
          try {
            const payload = await service.downloadTranscription(data.url);
            const audioBlob = data.audioUrl
              ? await fetchBlob(data.audioUrl)
              : undefined;
            isComplete = transcription.completed({
              isSuccess,
              transcriptionId,
              data: {
                ...payload,
                audioBlob,
              },
            });
          } catch (error) {
            transcription.completed({
              isSuccess: false,
              errorMessage: `${error}`,
              transcriptionId,
            });
            void service.delete(transcriptionId);
            return;
          }
        } else if (data?.audioUrl !== undefined) {
          const audioBlob = await fetchBlob(data.audioUrl);
          isComplete = transcription.completed({
            isSuccess,
            transcriptionId,
            data: { text: '', audioBlob },
          });
        } else {
          // Malformed message with neither transcript url nor audio url.
          // Delete the entity to avoid a resource leak.
          void service.delete(transcriptionId);
          return;
        }

        // Only delete the backend entity once the bridge has notified the extension.
        // Deleting early would remove the entity before the second file service
        // callback arrives, preventing the backend from sending the second
        // realtime message (audio or transcript) that the bridge is waiting for.
        if (isComplete) {
          void service.delete(transcriptionId);
        }
      },
    );
  }, []);

  useEffect(() => {
    const unsubscribes = [
      transcription.onStart(async (output): Promise<string> => {
        if (sessionId === undefined) {
          throw new Error('SessionId is not defined');
        }

        return service.start(sessionId, output);
      }),
      transcription.onStop(
        async (transcriptionId): Promise<void> => service.stop(transcriptionId),
      ),
      transcription.onCancel(
        async (transcriptionId): Promise<void> =>
          service.cancel(transcriptionId),
      ),
    ];
    const subscriber = subscribeToTranscriptionStatus();

    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
      subscriber.unsubscribe();
    };
  }, [sessionId]);
}
